<template>
  <div class="trk-week-chart-wrap">
    <div class="trk-week-chart" v-show="days.length">
      <canvas ref="canvas"></canvas>
    </div>
    <div class="trk-empty" v-if="!days.length">
      <strong>No days in this range</strong>
      Pick a date range to see consults booked each day.
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ymd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function eachDate(fromDate, toDate) {
  const dates = [];
  if (!fromDate || !toDate) {
    return dates;
  }
  const fromParts = String(fromDate).split('-').map(Number);
  const toParts = String(toDate).split('-').map(Number);
  if (fromParts.length !== 3 || toParts.length !== 3) {
    return dates;
  }
  const cur = new Date(fromParts[0], fromParts[1] - 1, fromParts[2]);
  const end = new Date(toParts[0], toParts[1] - 1, toParts[2]);
  if (Number.isNaN(cur.getTime()) || Number.isNaN(end.getTime()) || cur > end) {
    return dates;
  }
  while (cur <= end) {
    dates.push(ymd(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

function bucketDay(bucketStart) {
  const match = String(bucketStart || '').match(/(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

// One fixed colour per series (same inks as All journeys) so booked and completed never look alike.
const BOOKED_COLOR = '#0f6a7c';
const BOOKED_HOVER = '#0b5261';
const COMPLETED_COLOR = '#1f8a5b';
const COMPLETED_HOVER = '#176b3a';
const TARGET_COLOR = '#64748b';

function targetNote(value, goal) {
  if (!(goal > 0)) {
    return '';
  }
  return value >= goal ? ' (target met)' : ` (${goal - value} short of target)`;
}

function dailyByDate(dailyDays) {
  const map = {};
  (dailyDays || []).forEach((row) => {
    if (row && row.date) {
      map[row.date] = {
        booked: Number(row.booked) || 0,
        completed: Number(row.completed) || 0,
        cancelled: Number(row.cancelled) || 0
      };
    }
  });
  return map;
}

function ensureValueLabelsPlugin() {
  const plugins = Chart.pluginService.getAll();
  if (plugins.some((plugin) => plugin.id === 'trkBarValues')) {
    return;
  }
  Chart.pluginService.register({
    id: 'trkBarValues',
    afterDatasetsDraw(chart) {
      const ctx = chart.ctx || (chart.chart && chart.chart.ctx);
      if (!ctx) {
        return;
      }
      ctx.save();
      ctx.font = '700 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        if (dataset.type === 'line' || !dataset._trkShowValues) {
          return;
        }
        const meta = chart.getDatasetMeta(datasetIndex);
        ctx.fillStyle = datasetIndex === 0 ? '#0b5261' : '#176b3a';
        meta.data.forEach((bar, index) => {
          if (!bar || bar.hidden) {
            return;
          }
          const value = dataset.data[index];
          if (value == null || value === 0) {
            return;
          }
          ctx.fillText(String(value), bar._model.x, bar._model.y - 4);
        });
      });
      ctx.restore();
    }
  });
}

export default {
  name: 'WeeklyConsultsChart',
  props: {
    buckets: { type: Array, default: () => [] },
    dailyDays: { type: Array, default: () => [] },
    fromDate: { type: String, default: '' },
    toDate: { type: String, default: '' },
    goal: { type: Number, default: 25 }
  },
  data() {
    return { chart: null };
  },
  computed: {
    useDailySplit() {
      return (this.dailyDays || []).length > 0;
    },
    days() {
      const dailyMap = dailyByDate(this.dailyDays);
      const trafficTotals = {};
      (this.buckets || []).forEach((bucket) => {
        const day = bucketDay(bucket.bucketStart);
        if (!day) {
          return;
        }
        trafficTotals[day] = (trafficTotals[day] || 0) + Number(bucket.bookings || 0);
      });
      return eachDate(this.fromDate, this.toDate).map((date) => {
        const parts = date.split('-').map(Number);
        const local = new Date(parts[0], parts[1] - 1, parts[2]);
        const compact = this.rangeDays > 10;
        const daily = dailyMap[date];
        const booked = this.useDailySplit
          ? (daily ? daily.booked : 0)
          : (trafficTotals[date] || 0);
        const completed = this.useDailySplit && daily ? daily.completed : 0;
        return {
          date,
          booked,
          completed,
          cancelled: daily ? daily.cancelled : 0,
          label: compact
            ? `${parts[2]}/${parts[1]}`
            : `${WEEKDAYS[local.getDay()]} ${parts[2]}`
        };
      });
    },
    rangeDays() {
      return eachDate(this.fromDate, this.toDate).length;
    },
    chartKey() {
      return JSON.stringify({
        days: this.days.map((day) => [day.date, day.booked, day.completed]),
        goal: this.goal,
        split: this.useDailySplit
      });
    }
  },
  watch: {
    chartKey() {
      this.render();
    }
  },
  mounted() {
    ensureValueLabelsPlugin();
    this.render();
  },
  beforeDestroy() {
    this.destroyChart();
  },
  methods: {
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
    render() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.days.length) {
        this.destroyChart();
        return;
      }
      const goal = Number(this.goal) > 0 ? Number(this.goal) : 25;
      const bookedValues = this.days.map((day) => day.booked);
      const completedValues = this.days.map((day) => day.completed);
      const labels = this.days.map((day) => day.label);
      const maxValue = Math.max(goal, ...bookedValues, ...completedValues, 5);
      const datasets = [
        {
          type: 'bar',
          label: 'Booked',
          data: bookedValues,
          backgroundColor: BOOKED_COLOR,
          hoverBackgroundColor: BOOKED_HOVER,
          borderWidth: 0,
          maxBarThickness: this.days.length <= 8 ? 36 : 22,
          _trkShowValues: true
        },
        {
          type: 'line',
          label: `Daily target (${goal})`,
          data: bookedValues.map(() => goal),
          fill: false,
          borderColor: TARGET_COLOR,
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
          lineTension: 0
        }
      ];
      if (this.useDailySplit) {
        datasets.splice(1, 0, {
          type: 'bar',
          label: 'Consult completed',
          data: completedValues,
          backgroundColor: COMPLETED_COLOR,
          hoverBackgroundColor: COMPLETED_HOVER,
          borderWidth: 0,
          maxBarThickness: this.days.length <= 8 ? 36 : 22,
          _trkShowValues: true
        });
      }
      const config = {
        type: 'bar',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          title: { display: false },
          legend: {
            display: true,
            position: 'top',
            labels: { boxWidth: 14, fontSize: 12, fontStyle: '600', padding: 16 }
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              title: (items) => {
                const index = items && items[0] ? items[0].index : 0;
                const day = this.days[index];
                return day ? day.date : '';
              },
              label: (item, data) => {
                const dataset = data.datasets[item.datasetIndex];
                if (dataset.type === 'line') {
                  return `Daily target: ${item.yLabel}`;
                }
                if (dataset.label === 'Consult completed') {
                  return `${item.yLabel} consultations completed this day${targetNote(item.yLabel, goal)}`;
                }
                return `${item.yLabel} bookings paid this day${targetNote(item.yLabel, goal)}`;
              },
              afterBody: (items) => {
                const day = items && items[0] ? this.days[items[0].index] : null;
                return day && day.cancelled ? [`${day.cancelled} cancelled this day`] : [];
              }
            }
          },
          layout: { padding: { top: 28, right: 16, left: 10, bottom: 10 } },
          scales: {
            xAxes: [{
              categoryPercentage: 0.72,
              barPercentage: 0.85,
              gridLines: { display: false },
              ticks: {
                fontSize: this.days.length > 10 ? 11 : 13,
                fontStyle: 'bold',
                fontColor: '#17333a',
                maxRotation: this.days.length > 10 ? 45 : 0,
                minRotation: 0,
                autoSkip: false,
                padding: 10
              }
            }],
            yAxes: [{
              gridLines: { color: 'rgba(15, 93, 109, 0.12)', zeroLineColor: '#d5e0da' },
              ticks: {
                beginAtZero: true,
                precision: 0,
                suggestedMax: maxValue,
                fontColor: '#405048',
                callback: (value) => (Number.isInteger(value) ? value : null)
              },
              scaleLabel: {
                display: true,
                labelString: this.useDailySplit ? 'Consults per day' : 'Consults booked',
                fontStyle: '600',
                fontColor: '#17333a'
              }
            }]
          }
        }
      };
      this.destroyChart();
      this.chart = new Chart(canvas.getContext('2d'), config);
    }
  }
};
</script>
